import { Flex, Stepper, Button } from '@servicetitan/anvil2';
import { ImperativeForm } from '@servicetitan/financing-application-shared';
import {
    SetupDataStore,
    useProviderConfig,
    WorkflowButtonsStore,
    WorkflowStore,
} from '@servicetitan/financing-workflow-shared';
import { SelectedPlanStore } from '@servicetitan/financing-plans-shared';
import { RefObject, useCallback } from 'react';

interface ApplicationButtonsProps {
    formRef: RefObject<ImperativeForm>;
    isNextButtonDisabled?: boolean;
    showBackToPlanSelection?: boolean;
}

export const ApplicationButtons = ({
    formRef,
    isNextButtonDisabled,
    showBackToPlanSelection = false,
}: ApplicationButtonsProps) => {
    const { selectedPlan } = SelectedPlanStore.useStore();
    const { setWorkflowStage } = WorkflowStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const {
        useCustomerAuthFlow,
        useFinancingSubmission,
        continueToHardAppAfterSoft,
        defaultWorkflowStage,
        submitButtonLabel: configSubmitButtonLabel,
    } = useProviderConfig();

    const usePrequalSubmission = useFinancingSubmission === false;

    // Determine if we should use "Submit" button (soft prequal) vs "Submit Application" (hard prequal):
    // 1. Must be in prequal submission mode
    // 2. For prequals-landing flow (continueToHardAppAfterSoft: false): always use "Submit"
    // 3. For continue-to-hard flow (continueToHardAppAfterSoft: true): only use "Submit" during soft phase
    const shouldUseSoftPrequalButton = () => {
        if (!usePrequalSubmission) {
            return false;
        }
        // Prequals-landing flow: always use "Submit" (no hard phase)
        if (!continueToHardAppAfterSoft) {
            return true;
        } else if (!setupData?.prequalificationId) {
            // We are in soft prequal flow
            return true;
        }
        // Continue-to-hard flow: use "Submit" only during soft phase
        // (isApplyingForPrequalification must be explicitly true - undefined means hard app from banner)
        return setupData?.isApplyingForPrequalification === true;
    };

    const computedLabel = shouldUseSoftPrequalButton() ? 'Submit' : 'Submit Application';
    const submitButtonLabel = configSubmitButtonLabel ?? computedLabel;

    const nextHandler = useCallback(async () => {
        if (isNextButtonDisabled) {
            return false;
        }
        await formRef.current?.submit();
        const { resolve, promise } = Promise.withResolvers<boolean>();
        // There is some sort of concurrency issue with the form validation
        // so we need to wait a bit before checking if the form is valid
        setTimeout(() => {
            if (formRef.current && !formRef.current.isValid()) {
                resolve(false);
            } else {
                resolve(true);
            }
        }, 0);
        return promise;
    }, [formRef, isNextButtonDisabled]);

    const { CancelApplicationButton } = WorkflowButtonsStore.useStore();

    return (
        <Flex justifyContent="space-between">
            {showBackToPlanSelection ? (
                <Button
                    appearance="secondary"
                    onClick={() =>
                        setupData?.role === 'homeowner' && useCustomerAuthFlow
                            ? setWorkflowStage('customer-auth')
                            : setWorkflowStage(defaultWorkflowStage)
                    }
                >
                    Back
                </Button>
            ) : (
                <Stepper.PrevButton label="Back" />
            )}
            <Flex gap="3" justifyContent="flex-end" grow={1}>
                <CancelApplicationButton>Cancel</CancelApplicationButton>
                <Stepper.NextButton
                    nextLabel="Next"
                    completeLabel={submitButtonLabel}
                    disabled={
                        isNextButtonDisabled ??
                        (selectedPlan == null && !setupData?.isApplyingForPrequalification)
                    }
                    onClick={nextHandler as unknown as () => void}
                />
            </Flex>
        </Flex>
    );
};
